import { useState, useCallback, useEffect } from "react";
import { useURLState } from "./useURLState";

/**
 * Simplified pagination state management hook for Creative system
 * Only persists page numbers in localStorage, uses URL as primary source
 */
export const usePaginationState = () => {
  const { urlState } = useURLState();
  
  // Simplified state - only track page numbers, not tab state
  const [pageNumbers, setPageNumbers] = useState(() => {
    try {
      const saved = localStorage.getItem('creatives-page-numbers');
      return saved ? JSON.parse(saved) : {};
    } catch {
      return {};
    }
  });

  // Generate key for current tab/subtab combination
  const getCurrentKey = useCallback(() => {
    return `${urlState.tab}-${urlState.subtab}`;
  }, [urlState.tab, urlState.subtab]);

  // Get current page - URL is primary source, localStorage is fallback
  const getCurrentPage = useCallback(() => {
    // URL page takes precedence
    if (urlState.pageNumber > 0) {
      return urlState.pageNumber;
    }
    // Fallback to localStorage
    const key = getCurrentKey();
    return pageNumbers[key] || 1;
  }, [urlState.pageNumber, getCurrentKey, pageNumbers]);

  // Update pagination for specific tab/subtab (only in localStorage)
  const updatePagination = useCallback((tab, subtab, page) => {
    const key = `${tab}-${subtab}`;
    setPageNumbers(prev => {
      const newState = { ...prev, [key]: page };

      try {
        localStorage.setItem('creatives-page-numbers', JSON.stringify(newState));
      } catch (e) {
        console.warn('Failed to save page numbers:', e);
      }

      return newState;
    });
  }, []);

  // Update pagination for current tab/subtab
  const updateCurrentPagination = useCallback((page) => {
    updatePagination(urlState.tab, urlState.subtab, page);
  }, [updatePagination, urlState.tab, urlState.subtab]);

  // Reset pagination for specific tab/subtab
  const resetPagination = useCallback((tab, subtab) => {
    updatePagination(tab, subtab, 1);
  }, [updatePagination]);

  // Reset all pagination
  const resetAllPagination = useCallback(() => {
    setPageNumbers({});

    try {
      localStorage.removeItem('creatives-page-numbers');
    } catch (e) {
      console.warn('Failed to clear page numbers:', e);
    }
  }, []);

  // Sync URL page to localStorage (for session persistence)
  useEffect(() => {
    if (urlState.pageNumber > 0) {
      const currentKey = getCurrentKey();
      const storedPage = pageNumbers[currentKey];

      // Update localStorage if URL page is different
      if (urlState.pageNumber !== storedPage) {
        updateCurrentPagination(urlState.pageNumber);
      }
    }
  }, [urlState.pageNumber, getCurrentKey, pageNumbers, updateCurrentPagination]);

  // Get pagination info for all tabs (useful for debugging/analytics)
  const getAllPaginationInfo = useCallback(() => {
    return {
      current: {
        tab: urlState.tab,
        subtab: urlState.subtab,
        page: getCurrentPage()
      },
      stored: pageNumbers,
      url: urlState.pageNumber
    };
  }, [urlState, getCurrentPage, pageNumbers]);

  // Check if user has navigated away from page 1 in any tab
  const hasNavigatedFromFirstPage = useCallback(() => {
    return Object.values(pageNumbers).some(page => page > 1) || urlState.pageNumber > 1;
  }, [pageNumbers, urlState.pageNumber]);

  return {
    // Current state
    getCurrentPage,
    getCurrentKey,
    
    // Update functions
    updatePagination,
    updateCurrentPagination,
    
    // Reset functions
    resetPagination,
    resetAllPagination,
    
    // Info functions
    getAllPaginationInfo,
    hasNavigatedFromFirstPage,
    
    // Raw state (for advanced usage)
    pageNumbers
  };
};

export default usePaginationState;